/**
 * Register F3 — the stage drill-down audit, drawn.
 *
 * "Selecting a stage returns a chronological record of everything within it —
 * email received, opened, responded, broker call logged, notes, field edits
 * and by whom. This is the litigation record."
 *
 * The record arrives from GET /loans/{id}/stage-audit already grouped by the
 * stage segment that was open when each event happened. This panel renders
 * one segment at a time, selected from the stage strip, and always shows the
 * sources the platform does NOT capture — a record read in a dispute must be
 * honest about its own coverage.
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import {
  AlertTriangle,
  ArrowRight,
  FileEdit,
  Loader2,
  Mail,
  MessageSquare,
  Milestone,
  ScrollText,
} from 'lucide-react'

import { loansService, type StageAuditEntry, type StageAuditSegment } from '@/services/loans'

function stamp(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

function iconFor(kind: StageAuditEntry['kind']) {
  switch (kind) {
    case 'stage_change':
      return <Milestone size={12} className="text-amber-300" />
    case 'field_edit':
      return <FileEdit size={12} className="text-sky-300" />
    case 'note':
      return <MessageSquare size={12} className="text-emerald-300" />
    case 'email_received':
      return <Mail size={12} className="text-blue-300" />
    default:
      return <ScrollText size={12} className="text-stone-400" />
  }
}

function fmt(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function EntryRow({ entry }: { entry: StageAuditEntry }) {
  const d = entry.detail ?? {}
  const isEdit = entry.kind === 'field_edit' || entry.kind === 'stage_change'
  return (
    <li className="flex gap-2.5 rounded border border-stone-800/70 bg-black/20 px-2.5 py-2">
      <div className="mt-0.5 shrink-0">{iconFor(entry.kind)}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate text-[11px] text-stone-200">{entry.summary}</span>
          <span className="shrink-0 font-mono text-[10px] text-stone-500">{stamp(entry.at)}</span>
        </div>
        <div className="mt-0.5 text-[10px] text-stone-500">
          {/* "not recorded" is deliberate wording from the server — an actor
              is never guessed. */}
          by <span className={entry.actor === 'not recorded' ? 'italic text-stone-600' : 'text-stone-400'}>{entry.actor}</span>
        </div>
        {isEdit && (d.from !== undefined || d.to !== undefined) && (
          <div className="mt-1 flex items-center gap-1.5 font-mono text-[10px]">
            <span className="text-stone-500">{fmt(d.from)}</span>
            <ArrowRight size={10} className="text-stone-600" />
            <span className="text-stone-300">{fmt(d.to)}</span>
            {d.source ? <span className="ml-1 text-stone-600">· {String(d.source)}</span> : null}
          </div>
        )}
        {entry.kind === 'note' && d.body ? (
          <p className="mt-1 whitespace-pre-wrap text-[11px] text-stone-300">{String(d.body)}</p>
        ) : null}
        {entry.kind === 'email_received' && (d.from || d.subject) ? (
          <div className="mt-1 text-[10px] text-stone-500">
            {d.from ? <span>from {String(d.from)}</span> : null}
            {d.subject ? <span> · {String(d.subject)}</span> : null}
          </div>
        ) : null}
      </div>
    </li>
  )
}

export function StageAuditPanel({
  loanId,
  selectedIndex,
  onSelect,
}: {
  loanId: string
  /** Segment index chosen from the stage strip; null = current stage. */
  selectedIndex: number | null
  onSelect: (index: number) => void
}) {
  const audit = useQuery({
    queryKey: ['loan', loanId, 'stage-audit'],
    queryFn: () => loansService.getStageAudit(loanId),
    enabled: Boolean(loanId),
    retry: (count, err) => statusOf(err) !== 404 && count < 2,
  })

  const segments: StageAuditSegment[] = audit.data?.segments ?? []
  const [local, setLocal] = useState<number | null>(null)

  // Default to the current stage — the one the desk is looking at.
  useEffect(() => {
    if (segments.length === 0) return
    if (selectedIndex !== null) {
      setLocal(selectedIndex)
      return
    }
    setLocal((prev) => (prev === null ? segments.findIndex((s) => s.current) : prev))
  }, [segments, selectedIndex])

  const active = useMemo(
    () => (local !== null && local >= 0 ? segments[local] : undefined),
    [segments, local],
  )

  if (audit.isLoading) {
    return (
      <div className="flex h-48 items-center justify-center gap-2 text-[12px] text-stone-500">
        <Loader2 size={14} className="animate-spin" /> Loading the stage record…
      </div>
    )
  }
  if (audit.error && statusOf(audit.error) !== 404) {
    return <p className="py-6 text-center text-[11px] text-red-300">The stage record could not be loaded.</p>
  }
  if (segments.length === 0) {
    return <p className="py-6 text-center text-[11px] text-stone-500">No stage history recorded for this deal yet.</p>
  }

  return (
    <div className="space-y-3">
      {/* Stage selector — mirrors the strip above the tabs, but selection here
          drives which segment's record is shown. */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {segments.map((s, i) => (
          <button
            key={`${s.stage}-${s.entered_at}-${i}`}
            type="button"
            onClick={() => {
              setLocal(i)
              onSelect(i)
            }}
            className={clsx(
              'flex min-w-[112px] flex-col rounded border px-2 py-1 text-left transition-colors',
              i === local
                ? 'border-amber-400/50 bg-amber-400/10'
                : 'border-stone-800 bg-black/20 hover:border-stone-600',
            )}
          >
            <span className={clsx('truncate text-[10px] font-medium', i === local ? 'text-amber-200' : 'text-stone-300')}>
              {s.stage}
            </span>
            <span className="font-mono text-[10px] text-stone-500">
              {s.days}d · {s.event_count} event{s.event_count === 1 ? '' : 's'}
              {s.current ? ' · now' : ''}
            </span>
          </button>
        ))}
      </div>

      {active && (
        <section>
          <div className="mb-1.5 flex items-baseline justify-between">
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-stone-500">
              {active.stage} — {active.event_count} recorded event{active.event_count === 1 ? '' : 's'}
            </h3>
            <span className="font-mono text-[10px] text-stone-600">
              {stamp(active.entered_at)} → {active.exited_at ? stamp(active.exited_at) : 'now'}
            </span>
          </div>
          {active.events.length === 0 ? (
            <p className="rounded border border-stone-800/70 px-2.5 py-3 text-[11px] text-stone-600">
              Nothing was recorded while the deal sat in this stage.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {active.events.map((e, i) => (
                <EntryRow key={`${e.at}-${e.kind}-${i}`} entry={e} />
              ))}
            </ul>
          )}
        </section>
      )}

      {(audit.data?.unplaced_events?.length ?? 0) > 0 && (
        <section>
          <h3 className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-amber-300">
            Outside every stage
          </h3>
          <ul className="space-y-1.5">
            {audit.data!.unplaced_events.map((e, i) => (
              <EntryRow key={`u-${e.at}-${i}`} entry={e} />
            ))}
          </ul>
        </section>
      )}

      {/* Always shown. A record read in a dispute must say what it does not
          hold — a timeline that silently omitted these would read as complete. */}
      <section className="rounded border border-stone-800/70 bg-black/20 px-2.5 py-2">
        <div className="mb-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-stone-500">
          <AlertTriangle size={11} className="text-stone-500" /> Not captured on this record
        </div>
        <ul className="space-y-0.5 text-[10px] text-stone-500">
          {(audit.data?.uncaptured_sources ?? []).map((s) => (
            <li key={s.source}>
              <span className="font-mono text-stone-400">{s.source.replace(/_/g, ' ')}</span> — {s.detail}
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

function statusOf(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | null)?.response?.status
}

export default StageAuditPanel
